<template>
    <div ref="userDetail" class="user-detail" @scroll="onScroll">
        <Loading :loading="loading" />
        <template v-if="user">
            <UserDetailHeaderBar
                :from="from"
                :user="user"
                :userId="userId"
                :photos="userPhotos"
                :show="showHiddenHeader"
                :nickname="user.profile.nickname"
                @open="openActionSheet"
            />
            <UserDetailPremiumComment v-if="hasPremiumComment" :user="user" />
            <UserDetailPhotos :photos="userPhotos" />
            <UserDetailBase
                :user="user"
                :from="from"
                :agent="agent"
                :agentId="agentId"
                :agentPhoto="agentPhoto"
                :agentMessage="agentMessage"
                :status="status"
            />
            <UserProfile :user="user" :from="from" />
            <ProfilePassAcceptButtons
                class="bottom-btn-container message-btn"
                v-if="showProfilePassAcceptButtons"
                :count="remainProfilePhotoMessages.length"
                :message="profilePhotoMessage"
                :user="user"
                @action="onClickProfileActionButton"
            />
            <SuggestedPassAcceptButtons
                class="bottom-btn-container message-btn"
                v-else-if="showSuggestedPassAcceptButtons"
                :suggest="currentSuggest"
                :count="remainNextSuggests.length"
                @action="onClickSuggestedActionButton"
            />
        </template>
    </div>
</template>

<script>
import chatService from '@/services/chat'
import datingService from '@/services/dating'
import suggestService from '@/services/suggest'

import ProfilePassAcceptButtons from './components/ProfilePassAcceptButtons'
import SuggestedPassAcceptButtons from './components/SuggestedPassAcceptButtons'
import UserDetailBase from './components/UserDetailBase'
import UserDetailHeaderBar from './components/UserDetailHeaderBar'
import UserDetailPhotos from './components/UserDetailPhotos'
import UserProfile from './components/UserProfile'
import UserDetailPremiumComment from './components/UserDetailPremiumComment'

export default {
    name: 'UserDetailPage',
    components: {
        UserDetailBase,
        ProfilePassAcceptButtons,
        UserDetailHeaderBar,
        SuggestedPassAcceptButtons,
        UserDetailPhotos,
        UserProfile,
        UserDetailPremiumComment,
    },
    props: {
        from: String,
        userId: {
            type: Number,
            required: true,
        },
        agent: Object,
        agentId: Number,
        photos: {
            type: Array,
            default: () => [],
        },
        suggest: {
            type: Object,
            default: () => ({}),
        },
        agentPhoto: String,
        agentMessage: String,
        profilePhotoMessage: Object,
        status: String,
    },
    data: () => ({
        user: null,
        showHiddenHeader: false,
        loading: false,
        userPhotos: [],
    }),
    watch: {
        userId: function (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.init()
            }
        },
    },
    created() {
        this.init()
    },
    computed: {
        dom() {
            return this.$refs.userDetail
        },
        showProfilePassAcceptButtons() {
            return this.fromProfilePhotoComponent
        },
        showSuggestedPassAcceptButtons() {
            return this.fromSuggestedDatingPage
        },
        fromProfilePhotoComponent() {
            return this.from === 'ProfilePhoto'
        },
        fromSuggestedDatingPage() {
            return this.from === 'SuggestedDatingPage'
        },
        remainProfilePhotoMessages() {
            if (!this.fromProfilePhotoComponent) return []

            return (this.$store.getters.chat.$$messages || [])
                .filter(message => {
                    if (message.mtype !== 'profile-photo' || this.profilePhotoMessage.id >= message.id) {
                        return false
                    }

                    const content = this.$mustParse(message.content)
                    // show_photo 는 undefined 이거나 false 임
                    return !content.opened && !content.accept_status && content.show_photo === undefined
                })
                .reverse()
        },
        currentSuggest() {
            return this.suggest
        },
        remainNextSuggests() {
            const suggests = this.$store.getters.suggests || []

            if (!suggests || suggests.length === 0) return []

            return suggests.filter(suggest => {
                return this.suggest.id < suggest.id && suggest.status === 'request'
            })
        },
        hasPremiumComment() {
            // return false
            if ((this.user.premium_infos || []).length === 0) {
                return false
            }
            return this.user.premium_infos[0].premium_info.agent_intro || false
        },
    },
    methods: {
        async init() {
            if (!this.userId) {
                this.$stackRouter.pop()
                return
            }

            this.loading = true
            try {
                this.user = await this.$store.dispatch('loadUserDetail', { userId: this.userId })
            } catch (e) {
                if (this.fromSuggestedDatingPage) {
                    this.onClickSuggestedActionButton()
                } else if (this.fromProfilePhotoComponent) {
                    this.onClickProfileActionButton()
                } else {
                    this.$toast.error(e.data)
                    this.$stackRouter.pop()
                    return
                }
            } finally {
                this.loading = false
            }

            if (this.photos.length === 0) {
                this.userPhotos = [...this.user.photos] || []
            } else {
                const parsedPhotos = this.photos.map(p => {
                    if (p.url.indexOf('thumb') > 0) {
                        const idx = p.url.indexOf('thumb')
                        const front = p.url.slice(0, idx)
                        const back = p.url.slice(idx + 5)

                        return {
                            url: front + 'medium' + back,
                        }
                    }
                    return { ...p }
                })

                this.userPhotos = [...parsedPhotos]
            }

            this.$scroll.up(this.dom, true)
        },
        onScroll(event) {
            this.showHiddenHeader = event.target.scrollTop > window.innerWidth - 56 // 56 is @header-height
        },
        asString(obj) {
            return typeof obj === 'string' ? obj : obj.name
        },
        async onClickProfileActionButton(status) {
            if (status === 'undo') return

            if (this.remainProfilePhotoMessages.length > 0) {
                const photos = () => {
                    let photos = []
                    if (content.url) {
                        photos.push(content.url)
                    }

                    if (content.urls) {
                        photos = photos.concat(content.urls)
                    }

                    return photos.map(p => ({ url: p }))
                }

                const nextMessage = this.remainProfilePhotoMessages[0]
                const content = this.$mustParse(nextMessage.content)

                await chatService.openProfile(this.$store.getters.chat.id, nextMessage.id)
                content.opened = true
                const message = this.$store.getters.chat.$$messages.find(message => message.id === nextMessage.id)
                message.content = JSON.stringify(content)

                this.$stackRouter.replace({
                    name: 'UserDetailPage',
                    props: {
                        userId: content.source_id,
                        photos: photos(),
                        from: 'ProfilePhoto',
                        profilePhotoMessage: nextMessage,
                    },
                })
            } else {
                this.$stackRouter.pop()
            }
        },
        async onClickSuggestedActionButton(status) {
            if (this.remainNextSuggests.length === 0) {
                this.$stackRouter.pop()
            } else {
                const nextSuggest = this.remainNextSuggests[0]

                if (!nextSuggest.user_opened) {
                    await suggestService.update(nextSuggest.id, { user_opened: true })
                    this.$store.commit('setSuggest', { id: nextSuggest.id })
                }

                const photos = [{ url: nextSuggest.photo1 }, { url: nextSuggest.photo2 }]

                this.$stackRouter.replace({
                    name: 'UserDetailPage',
                    props: {
                        from: 'SuggestedDatingPage',
                        userId: nextSuggest.source_id,
                        photos,
                        suggest: nextSuggest,
                        agent: nextSuggest.agent,
                        agentId: nextSuggest.agent_id,
                        agentMessage: nextSuggest.message,
                        agentPhoto: nextSuggest.agent.photo_url,
                    },
                })
            }
        },
        openActionSheet() {
            this.$actionSheet({
                buttons: [
                    {
                        label: 'LEAVE_DATING_FEEDBACK',
                        handler: async () => {
                            try {
                                this.$loading(true)
                                const dating = await datingService.datings(this.user.id)

                                if ((dating || []).length === 0) {
                                    this.$toast.error('소개팅 성사된 회원끼리만 피드백을 남길 수 있어요!')

                                    return
                                }

                                if (dating[0].is_feedback) {
                                    this.$modal.basic({
                                        title: 'MODAL_DEFAULT_TITLE',
                                        body: this.$translate('ALREADY_SUBMIT_FEEDBACK'),
                                        buttons: [
                                            {
                                                label: 'CONFIRM',
                                                class: 'btn-primary',
                                            },
                                        ],
                                    })

                                    return
                                }

                                const dateDiff = this.$options.filters.dateDiff(dating[0].created_at)

                                if (dateDiff > 20) {
                                    this.$toast.error('피드백은 소개팅 성사 후 20일 동안만 가능해요')

                                    return
                                }
                                this.$stackRouter.push({
                                    name: 'DatingStatusCheckPage',
                                    props: {
                                        user: this.user,
                                        photos: this.userPhotos,
                                        dating: dating[0],
                                    },
                                })
                            } catch (e) {
                                console.error(e)
                            } finally {
                                this.$loading(false)
                            }
                        },
                    },
                    {
                        label: 'REPORT_PROFILE',
                        class: 'c-red',
                        handler: () => {
                            this.$stackRouter.push({
                                name: 'ReportSelectReasonPage',
                                props: {
                                    type: 'user',
                                    target: this.user,
                                },
                            })
                        },
                    },
                ],
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.user-detail {
    overflow-y: auto;

    hr {
        border-bottom: solid 1px $grey-02;
    }

    .user-profile {
        padding-bottom: 40px;
    }

    ::v-deep .bottom-btn-container {
        position: sticky;
        bottom: 0;
        width: 100%;
        background: white;
        border-top: 1px solid $grey-02;
        padding: 16px;
        z-index: 10;

        .desc {
            font-size: 12px;
            line-height: 16px;
            text-align: center;
            margin-bottom: 12px;
        }

        button {
            height: 48px;
            font-size: 15px;
            opacity: 1;

            &.btn-reject {
                width: 48px;
                background: $grey-05;

                i {
                    color: white;
                }
            }
        }

        &.message-btn {
            .btn-brd {
                flex: 1;
                margin-right: 8px;

                &.btn-pass {
                    max-width: 100px;
                }
            }

            .btn-primary {
                flex: 2;
                box-shadow: none;
            }
        }
    }
}
</style>
